import { getProjectTypeConfig } from '../projectTypeConfig';

/**
 * Dashboard project filtering — pure functions so the behavior is testable
 * independent of the view.
 */

export const DEFAULT_PROJECT_FILTERS = Object.freeze({
  query: '',
  type: 'all',
  status: 'all',
});

export const PROJECT_STATUS_LABELS = Object.freeze({
  draft: 'Draft',
  in_progress: 'In Progress',
  completed: 'Completed',
  published: 'Published',
});

export const hasActiveFilters = (filters) =>
  Boolean((filters.query || '').trim()) || filters.type !== 'all' || filters.status !== 'all';

/**
 * @param {Array} projects
 * @param {{ query: string, type: string, status: string }} filters
 * @returns {Array} projects matching every active filter
 */
export const filterProjects = (projects, filters) => {
  const query = (filters.query || '').trim().toLowerCase();

  return (projects || []).filter((project) => {
    if (!project) return false;

    if (filters.type !== 'all' && (project.type || 'other') !== filters.type) {
      return false;
    }
    if (filters.status !== 'all' && (project.status || 'draft') !== filters.status) {
      return false;
    }
    if (query) {
      const haystack = [project.title, project.description, ...(project.tags || [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
};

/**
 * Type filter options limited to types that actually occur in the user's
 * projects — no point offering filters guaranteed to return nothing.
 * @returns {Array<{ value: string, label: string }>}
 */
export const getPresentTypeOptions = (projects) => {
  const seen = new Set();
  const options = [];
  (projects || []).forEach((project) => {
    const type = project?.type || 'other';
    if (seen.has(type)) return;
    seen.add(type);
    options.push({ value: type, label: getProjectTypeConfig(type).label });
  });
  return options.sort((a, b) => a.label.localeCompare(b.label));
};

export const DEFAULT_PROJECT_SORT = 'edited';

export const PROJECT_SORT_OPTIONS = [
  { value: 'edited', label: 'Last edited' },
  { value: 'created', label: 'Recently created' },
  { value: 'title', label: 'Title A–Z' },
  { value: 'words', label: 'Word count' },
];

/** Stable-sorted copy of `projects` by the given sort key. */
export const sortProjects = (projects, sortKey) => {
  const list = [...(projects || [])];
  switch (sortKey) {
    case 'title':
      list.sort((a, b) => (a?.title || '').localeCompare(b?.title || ''));
      break;
    case 'words':
      list.sort((a, b) => (b?.wordCount || 0) - (a?.wordCount || 0));
      break;
    case 'created':
      list.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
      break;
    case 'edited':
    default:
      list.sort((a, b) => new Date(b?.updatedAt || 0) - new Date(a?.updatedAt || 0));
      break;
  }
  return list;
};

/** Status filter options limited to statuses present in the projects. */
export const getPresentStatusOptions = (projects) => {
  const seen = new Set();
  const options = [];
  (projects || []).forEach((project) => {
    const status = project?.status || 'draft';
    if (seen.has(status)) return;
    seen.add(status);
    options.push({ value: status, label: PROJECT_STATUS_LABELS[status] || status });
  });
  const order = Object.keys(PROJECT_STATUS_LABELS);
  return options.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
};
